/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {PatientsDataModule} from "@modules/PatientDataModule";
import {Card} from "../../widget/Card";
import {CardHeader} from "../../widget/CardHeader";
import {HGap} from "../../widget/HGap";
import {IconInfo} from "../../widget/IconInfo";
import {
	hideTooltip,
	showTooltip
} from "../../widget/appTooltip";
import {COLORS} from "@res/colors";
import {
	margin_b_lg,
	margin_rl_lg,
	margin_t_lg
} from "@styles/styles";
import {
	PsychologicalMemoryNames,
	psychologicalTypeMap,
	TableKeys
} from "@app/app-shared/patient-data";
import {DB_Patient} from "@app/app-shared/patient";
import {GraphPanelWrapper} from "./GraphPanelWrapper";
import { PsychologicalDataTable } from "../PsychologicalDataTable";

type Props = {
	patient: DB_Patient;
	type: PsychologicalMemoryNames;
}

export class PsychologicalTableGraph
	extends BaseComponent<Props> {

	resolveTooltipText = () => {
		switch (this.props.type) {
			case PsychologicalMemoryNames.Depression:
				return `The purpose of the PHQ-2 is to screen for depression in a “first-step” approach. ` +
					`If the score is 3 or greater, major depressive disorder is likely. Patients who screen positive should ` +
					`be further evaluated with the PHQ-9, other diagnostic instruments, or direct interview to determine whether ` +
					`they meet criteria for a depressive disorder.`;
			case PsychologicalMemoryNames.Anxiety:
				return `The Generalized Anxiety Disorder 2-item (GAD-2) is a brief initial screening tool ` +
					`for generalized anxiety disorder.<br/>` +
					`Although designed as a screening tool for generalized anxiety, the GAD-2 also performs as a screening ` +
					`tool for Panic Disorder, Social Anxiety Disorder, and Posttraumatic Stress Disorder.`;
			default:
				return "";
		}
	};

	resolveTitle = () => {
		switch (this.props.type) {
			case PsychologicalMemoryNames.Depression:
				return "PHQ-2 Score";
			case PsychologicalMemoryNames.Anxiety:
				return "GAD-2 Score";
			default:
				return "";
		}
	};

	resolveQuestions = () => {
		const mapValues = psychologicalTypeMap(this.props.type);
		if (!mapValues)
			return [];

		return mapValues.questions;
	};

	render() {
		const {patient, type} = this.props;
		const psychologicalData = patient ? PatientsDataModule.getData(patient.unitId, TableKeys.PsychologicalAssessment) : undefined;

		if (!psychologicalData?.data)
			return null;

		const questions = this.resolveQuestions();

		return <Card>
			<CardHeader>
				<div className={"ll_h_c"}>
					{GraphPanelWrapper.title(this.resolveTitle())}
					<HGap h={10}/>
					<IconInfo
						onMouseOver={(e) => {
							const rect = e.currentTarget.getBoundingClientRect();
							showTooltip(e, this.resolveTooltipText(),
							            rect.left,
							            rect.top,
							            {
								            zIndex: 30,
								            textAlign: "left",
								            backgroundColor: "white",
								            borderRadius: "4px",
								            transform: "translate(10px, 10px)",
								            width: 600,
								            border: `1px solid ${COLORS.primary_light_blue}`
								            // ...this.props.tooltipStyle
							            });
						}}
						onMouseLeave={(_e) => {
							hideTooltip();
							return;
						}}
					/>

				</div>
			</CardHeader>
			<div className={`${margin_rl_lg} ${margin_t_lg} ${margin_b_lg}`}>
				<PsychologicalDataTable
					data={PatientsDataModule.preparePsychologicalAssessmentData(psychologicalData.data, questions, type)}
					questions={questions}/>
			</div>
		</Card>;
	}


}
